import { useCallback, useEffect, useMemo, useState } from 'react'
import { useVersionReportVersionRepository } from '../../../../services/adaptors/versionReportVersionRepositoryAdaptor'
import {
  VersionReportVersionEntity,
  VersionReportVersionPageContent,
  VersionReportVersionPageEntity,
} from '../../../../domain/entity/version-report/VersionReportVersionEntity'
import { VersionReportPageConfig } from '../../../../domain/entity/version-report/VersionReportTemplateEntity'
import { usePageState } from './pageState'
import { useUrlQuery } from './useUrlQuery'

export type UseCurrentVersionReturnValue = ReturnType<typeof useCurrentVersion>
export type CurrentReport = {
  versionUuid: string | undefined
  updateVersionUuid: (v: string | undefined) => void
  currentVersion: VersionReportVersionEntity | undefined
  currentPage: VersionReportVersionPageEntity | undefined
}
export const useCurrentVersion = (uuid: string, projectUuid: string) => {
  const [versionUuid, setVersionUuid] = useState<string | undefined>()
  const [currentVersion, setCurrentVersion] = useState<
    VersionReportVersionEntity | undefined
  >()
  const { fetch, reCalculateSnapshot } = useVersionReportVersionRepository()
  const [currentPageIndex, setCurrentPageIndex] = useState<number>(0)
  const [initialized, setInitialized] = useState<boolean>(false)

  const { versionUuidFromUrl } = useUrlQuery()
  const {
    storedVersionUuid,
    store,
    initialized: pageStateInitialized,
  } = usePageState(uuid, projectUuid)

  const updateVersionUuid = useCallback(
    (newVersionUuid: string | undefined) => {
      setVersionUuid(newVersionUuid)
    },
    []
  )

  const currentPage = useMemo(() => {
    if (!currentVersion) return undefined
    return currentVersion.pages[currentPageIndex]
  }, [currentPageIndex, currentVersion])

  useEffect(() => {
    const fn = async () => {
      if (!versionUuid) {
        setCurrentVersion(undefined)
        setCurrentPageIndex(0)
        const noDataStored = pageStateInitialized && !storedVersionUuid
        noDataStored && setInitialized(true)
        return
      }
      const response = await fetch(versionUuid)
      setCurrentVersion(response)
      pageStateInitialized && setInitialized(true)
    }
    fn()
  }, [fetch, versionUuid, pageStateInitialized])

  useEffect(() => {
    if (!pageStateInitialized) return
    if (versionUuidFromUrl) {
      setVersionUuid(versionUuidFromUrl)
    } else if (storedVersionUuid) {
      setVersionUuid(storedVersionUuid)
    }
  }, [
    pageStateInitialized,
    storedVersionUuid,
    setVersionUuid,
    versionUuidFromUrl,
  ])

  useEffect(() => {
    if (!versionUuid) return
    store({ versionUuid })
  }, [versionUuid, store])

  const updatePage = useCallback(
    <
      K extends keyof VersionReportVersionPageEntity,
      V extends VersionReportVersionPageEntity[K],
    >(
      path: K,
      value: V
    ) => {
      setCurrentVersion(prev => {
        if (!prev) return prev
        const newPages = prev.pages.map((page, index) => {
          if (index === currentPageIndex) {
            const newPage = {
              ...page,
            }
            newPage[path] = value
            return newPage
          }
          return page
        })
        return {
          ...prev,
          pages: newPages,
        }
      })
    },
    [currentPageIndex]
  )
  const updatePageTitle = useCallback(
    (newValue: string) => {
      updatePage('pageTitle', newValue)
    },
    [updatePage]
  )
  const updateLeadSentence = useCallback(
    (newValue: string) => {
      updatePage('leadSentence', newValue)
    },
    [updatePage]
  )
  const updatePageConfig = useCallback(
    <
      K extends keyof VersionReportPageConfig,
      T extends VersionReportPageConfig[K],
    >(
      path: K,
      value: T
    ) => {
      setCurrentVersion(prev => {
        if (!prev) return prev
        const newPages = prev.pages.map((page, index) => {
          if (index === currentPageIndex) {
            const newConfig = {
              ...page.config,
            }
            newConfig[path] = value
            const newPage = {
              ...page,
              config: newConfig,
            }
            return newPage
          }
          return page
        })
        return {
          ...prev,
          pages: newPages,
        }
      })
    },
    [currentPageIndex]
  )

  const updatePageContent = useCallback(
    <
      K extends keyof VersionReportVersionPageContent,
      T extends VersionReportVersionPageContent[K],
    >(
      path: K,
      value: T
    ) => {
      setCurrentVersion(prev => {
        if (!prev) return prev
        const newPages = prev.pages.map((page, index) => {
          if (index === currentPageIndex) {
            const newContent = {
              ...page.content,
            }
            newContent[path] = value
            const newPage = {
              ...page,
              content: newContent,
            }
            return newPage
          }
          return page
        })
        return {
          ...prev,
          pages: newPages,
        }
      })
    },
    [currentPageIndex]
  )

  const toNextPage = useCallback(() => {
    if (!currentVersion) return
    setCurrentPageIndex(prev => {
      if (prev === currentVersion.pages.length - 1) {
        return 0
      }
      return prev + 1
    })
  }, [currentVersion])
  const toPrevPage = useCallback(() => {
    if (!currentVersion) return
    setCurrentPageIndex(prev => {
      if (prev === 0) {
        return currentVersion.pages.length - 1
      }
      return prev - 1
    })
  }, [currentVersion])
  const reCalculateAndFetchVersion = useCallback(async () => {
    if (!versionUuid) return
    await reCalculateSnapshot(versionUuid)
    const response = await fetch(versionUuid)
    setCurrentVersion(response)
  }, [versionUuid, reCalculateSnapshot, fetch])
  return {
    versionUuid,
    updateVersionUuid,
    currentPage,
    currentVersion,
    updatePageTitle,
    updateLeadSentence,
    updatePageConfig,
    updatePageContent,
    toNextPage,
    toPrevPage,
    reCalculateAndFetchVersion,
    storedVersionUuid,
    initialized,
  }
}
